import { useState, useEffect, useMemo } from 'react'
import { Link, useSearchParams, useParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { supabase } from '@/integrations/supabase/client'
import type { Database } from '@/integrations/supabase/types'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { Search, MapPin, DollarSign, TrendingUp, Filter, SlidersHorizontal, X, Sparkles, Heart } from 'lucide-react'
import { BUSINESS_SECTORS } from '@/utils/businessSectors'
import { getListingImage } from '@/utils/getListingImage'
import { resolveListingImages } from '@/utils/resolveListingImages'
import { Helmet } from 'react-helmet-async'
import { useToast } from '@/hooks/use-toast'
import { ListingImage } from '@/components/ui/ListingImage'

type Listing = Database['public']['Views']['v_listings_with_image']['Row'] & { price: number | null }

const Explore = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [listings, setListings] = useState<Listing[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [sectorFilter, setSectorFilter] = useState('all')
  const [stateFilter, setStateFilter] = useState('all')
  const [minPrice, setMinPrice] = useState('')
  const [maxPrice, setMaxPrice] = useState('')
  const [sortBy, setSortBy] = useState('newest')
  const [availableSectors, setAvailableSectors] = useState<string[]>([])
  const [availableStates, setAvailableStates] = useState<string[]>([])
  const [showFilters, setShowFilters] = useState(false)
  const [activeFiltersCount, setActiveFiltersCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [user, setUser] = useState<any>(null)
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set())
  const { toast } = useToast()
  
  const PAGE_SIZE = 12
  const params = useParams<{ page?: string }>()
  const navigate = useNavigate()
  const pageFromPath = params.page ? parseInt(params.page, 10) : NaN
  const pageFromQuery = parseInt(searchParams.get('page') || '1', 10)
  const currentPageFromUrl = Math.max(1, !isNaN(pageFromPath) ? pageFromPath : pageFromQuery)
  const isQueryPagination = isNaN(pageFromPath) && currentPageFromUrl > 1
  const totalPages = Math.ceil(totalCount / PAGE_SIZE)

  // Detect if we have indexable facets (single state or sector)
  const hasIndexableFacets = (sectorFilter !== 'all' && stateFilter === 'all') || 
                             (stateFilter !== 'all' && sectorFilter === 'all')
  const hasMultipleFacets = sectorFilter !== 'all' && stateFilter !== 'all'
  const shouldNoIndex = hasMultipleFacets || (minPrice || maxPrice) || searchTerm || currentPageFromUrl >= 6 || isQueryPagination

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      setUser(session?.user ?? null)
      if (session?.user) {
        fetchFavorites(session.user.id)
      }
    }
    checkUser()
  }, [])

  useEffect(() => {
    setCurrentPage(currentPageFromUrl)
  }, [searchParams, params.page, currentPageFromUrl])

  useEffect(() => {
    fetchListings()
    fetchFilterOptions()
  }, [searchTerm, sectorFilter, stateFilter, minPrice, maxPrice, sortBy, currentPage])

  useEffect(() => {
    // Count active filters
    let count = 0
    if (searchTerm) count++
    if (sectorFilter !== 'all') count++
    if (stateFilter !== 'all') count++
    if (minPrice || maxPrice) count++
    setActiveFiltersCount(count)
  }, [searchTerm, sectorFilter, stateFilter, minPrice, maxPrice])

  // Reset to page 1 when search or filters change
  useEffect(() => {
    setCurrentPage(1)
    const params = new URLSearchParams(searchParams)
    if (params.has('page')) {
      params.delete('page')
      setSearchParams(params)
    }
  }, [searchTerm, sectorFilter, stateFilter, minPrice, maxPrice, sortBy])

  const fetchFavorites = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('favorites')
        .select('listing_id')
        .eq('user_id', userId)
      
      if (data) {
        setFavoriteIds(new Set(data.map(f => f.listing_id)))
      }
    } catch (error) {
      console.error('Error fetching favorites:', error)
    }
  }

  const toggleFavorite = async (listingId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to save favorites",
        variant: "destructive"
      })
      return
    }

    const isFavorited = favoriteIds.has(listingId)

    try {
      if (isFavorited) {
        await supabase
          .from('favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('listing_id', listingId)
        
        setFavoriteIds(prev => {
          const newSet = new Set(prev)
          newSet.delete(listingId)
          return newSet
        })
        toast({ title: "Removed from favorites" })
      } else {
        await supabase
          .from('favorites')
          .insert({ user_id: user.id, listing_id: listingId })
        
        setFavoriteIds(prev => new Set(prev).add(listingId))
        toast({ title: "Added to favorites!" })
      }
    } catch (error) {
      console.error('Error toggling favorite:', error)
      toast({
        title: "Error",
        description: "Failed to update favorites",
        variant: "destructive"
      })
    }
  }

  const fetchFilterOptions = async () => {
    try {
      // Use comprehensive sectors list
      setAvailableSectors(BUSINESS_SECTORS)

      // Fetch unique states
      const { data: statesData } = await supabase
        .from('listings')
        .select('location_state')
        .eq('status', 'live')
        .not('location_state', 'is', null)

      const uniqueStates = Array.from(new Set(statesData?.map(item => item.location_state) || []))
      setAvailableStates(uniqueStates)
    } catch (error) {
      console.error('Error fetching filter options:', error)
    }
  }

  const fetchListings = async () => {
    setLoading(true)
    // Clear previous results to avoid stale items during new searches/filters
    setListings([])
    try {
      // If any filters/search are active and we're not on page 1, jump to page 1 to prevent 416s
      if ((searchTerm || sectorFilter !== 'all' || stateFilter !== 'all' || minPrice || maxPrice) && currentPage > 1) {
        setCurrentPage(1)
        return
      }
      let query = supabase
        .from('v_listings_with_image')
        .select('*', { count: 'exact' })
        .eq('status', 'live')

      // Apply search filter - only search in title (case-insensitive)
      const term = (searchTerm || '').trim()
      if (term) {
        query = query.ilike('title', `%${term}%`)
      }

      // Apply sector filter
      if (sectorFilter && sectorFilter !== 'all') {
        query = query.eq('sector', sectorFilter)
      }

      // Apply state filter
      if (stateFilter && stateFilter !== 'all') {
        query = query.eq('location_state', stateFilter)
      }

      // Apply price filters
      if (minPrice) {
        const minPriceNum = parseFloat(minPrice)
        if (!isNaN(minPriceNum)) {
          query = query.gte('price', minPriceNum)
        }
      }
      if (maxPrice) {
        const maxPriceNum = parseFloat(maxPrice)
        if (!isNaN(maxPriceNum)) {
          query = query.lte('price', maxPriceNum)
        }
      }

      // Apply sorting
      if (sortBy === 'price-low') {
        query = query
          .order('price', { ascending: true, nullsFirst: false })
          .order('created_at', { ascending: false })
          .order('id', { ascending: false })
      } else if (sortBy === 'price-high') {
        query = query
          .order('price', { ascending: false, nullsFirst: false })
          .order('created_at', { ascending: false })
          .order('id', { ascending: false })
      } else {
        query = query
          .order('created_at', { ascending: false })
          .order('id', { ascending: false })
      }

      // Apply pagination
      const from = (currentPage - 1) * PAGE_SIZE
      const to = from + PAGE_SIZE - 1
      query = query.range(from, to)

      const { data, error, count } = await query

      if (error) throw error

      const raw = data || []
      const filtered = term
        ? raw.filter((l) => (l.title || '').toLowerCase().includes(term.toLowerCase()))
        : raw

      setListings(filtered)
      setTotalCount(count || filtered.length)
    } catch (error) {
      console.error('Error fetching listings:', error)
      // If an out-of-range page caused the error, reset to first page
      if (currentPage > 1) {
        setCurrentPage(1)
      }
    } finally {
      setLoading(false)
    }
  }

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages) return
    
    const newParams = new URLSearchParams(searchParams)
    if (hasIndexableFacets && page <= 5) {
      // Use path-based pagination for indexable routes
      let path = '/explore'
      if (sectorFilter !== 'all') path += `/${sectorFilter.toLowerCase().replace(/\s+/g, '-')}`
      if (stateFilter !== 'all') path += `/${stateFilter.toLowerCase()}`
      if (page > 1) path += `/page/${page}`
      navigate(path)
    } else {
      // Use query param for non-indexable combinations
      if (page === 1) {
        newParams.delete('page')
      } else {
        newParams.set('page', page.toString())
      }
      setSearchParams(newParams)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const clearAllFilters = () => {
    setSearchTerm('')
    setSectorFilter('all')
    setStateFilter('all')
    setMinPrice('')
    setMaxPrice('')
    setSortBy('newest')
    setSearchParams({})
    navigate('/explore')
  }

  const formatPrice = (price: number | null) => {
    if (price === null) return 'Price on Application'
    return `$${price.toLocaleString()}`
  }

  const truncateText = (text: string, maxLength: number = 120) => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength).trim() + '...'
  }

  // Remove duplicate listings by listing_number (or title+sector+location as fallback)
  const displayedListings = useMemo(() => {
    const seen = new Set<string>()
    const out: Listing[] = []
    for (const l of listings || []) {
      const num = (l.listing_number || '').replace(/[^0-9]/g, '')
      const fallbackKey = `${(l.title || '').trim()}|${(l.sector || '').trim()}|${(l.location_suburb || '').trim()}|${(l.location_state || '').trim()}`
      const key = (num || fallbackKey).toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      out.push(l)
    }
    return out
  }, [listings])

  // Resolve unique, category-appropriate demo images per page
  const imageMap = useMemo(() => (
    resolveListingImages(
      (displayedListings || []).map(l => ({
        id: String(l.id),
        title: l.title || '',
        sector: l.sector || '',
        current: l.images?.[0] || null,
      }))
    )
  ), [displayedListings])

  // De-duplicate teasers and generate sensible fallbacks per listing
  const teaserMap = useMemo(() => {
    const seen = new Map<string, number>()
    const map: Record<string, string> = {}
    ;(displayedListings || []).forEach(l => {
      const id = String(l.id)
      const base = (l.teaser || '').trim()
      const key = base.toLowerCase()
      const cnt = key ? (seen.get(key) || 0) : 0
      if (key) seen.set(key, cnt + 1)
      if (!base || cnt > 0) {
        const parts = [l.sector, l.location_suburb, l.location_state].filter(Boolean).join(' • ')
        const source = (l.description || '').trim() || `${l.title} — ${parts}`
        map[id] = source
      } else {
        map[id] = base
      }
    })
    return map
  }, [displayedListings])

  // Build canonical URL logic
  const buildCanonicalUrl = () => {
    let path = 'https://www.bizmatch.com.au/explore'
    if (sectorFilter !== 'all') path += `/${sectorFilter.toLowerCase().replace(/\s+/g, '-')}`
    if (stateFilter !== 'all') path += `/${stateFilter.toLowerCase()}`
    if (currentPageFromUrl > 1 && currentPageFromUrl <= 5) path += `/page/${currentPageFromUrl}`
    return path + '/'
  }

  const buildPrevUrl = () => {
    if (currentPageFromUrl <= 1) return null
    let path = 'https://www.bizmatch.com.au/explore'
    if (sectorFilter !== 'all') path += `/${sectorFilter.toLowerCase().replace(/\s+/g, '-')}`
    if (stateFilter !== 'all') path += `/${stateFilter.toLowerCase()}`
    if (currentPageFromUrl > 2) path += `/page/${currentPageFromUrl - 1}`
    return path + '/'
  }

  const buildNextUrl = () => {
    if (currentPageFromUrl >= totalPages || currentPageFromUrl >= 5) return null
    let path = 'https://www.bizmatch.com.au/explore'
    if (sectorFilter !== 'all') path += `/${sectorFilter.toLowerCase().replace(/\s+/g, '-')}`
    if (stateFilter !== 'all') path += `/${stateFilter.toLowerCase()}`
    path += `/page/${currentPageFromUrl + 1}`
    return path + '/'
  }

  // Dynamic page title and description
  const pageTitle = (() => {
    let title = 'Explore Businesses for Sale'
    if (sectorFilter !== 'all' && stateFilter !== 'all') {
      title = `${sectorFilter} Businesses for Sale in ${stateFilter}`
    } else if (sectorFilter !== 'all') {
      title = `${sectorFilter} Businesses for Sale in Australia`
    } else if (stateFilter !== 'all') {
      title = `Businesses for Sale in ${stateFilter}`
    }
    if (currentPageFromUrl > 1) title += ` - Page ${currentPageFromUrl}`
    return title + ' | BizMatch'
  })()

  const pageDescription = (() => {
    let desc = `Explore ${totalCount} businesses for sale`
    if (sectorFilter !== 'all' && stateFilter !== 'all') {
      desc = `Find ${sectorFilter.toLowerCase()} businesses for sale in ${stateFilter}. Browse ${totalCount} verified listings with detailed financials.`
    } else if (sectorFilter !== 'all') {
      desc = `Discover ${sectorFilter.toLowerCase()} businesses for sale across Australia. ${totalCount} verified opportunities available.`
    } else if (stateFilter !== 'all') {
      desc = `Browse businesses for sale in ${stateFilter}. ${totalCount} verified listings with complete business information.`
    } else {
      desc = `Explore ${totalCount} verified businesses for sale across Australia. Find your perfect opportunity with detailed financial insights.`
    }
    return desc.substring(0, 160)
  })()

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{pageTitle}</title>
        <link rel="canonical" href={buildCanonicalUrl()} />
        {buildPrevUrl() && <link rel="prev" href={buildPrevUrl()!} />}
        {buildNextUrl() && <link rel="next" href={buildNextUrl()!} />}
        <meta name="description" content={pageDescription} />
        <meta name="robots" content={shouldNoIndex ? 'noindex, follow' : 'index, follow'} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={buildCanonicalUrl()} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content="https://www.bizmatch.com.au/bizmatch-logo-transparent.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={buildCanonicalUrl()} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://www.bizmatch.com.au/bizmatch-logo-transparent.png" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: pageTitle,
          description: pageDescription,
          url: buildCanonicalUrl(),
          mainEntity: {
            '@type': 'ItemList',
            numberOfItems: totalCount,
            itemListElement: displayedListings.slice(0, 10).map((listing, idx) => ({
              '@type': 'ListItem',
              position: idx + 1,
              url: `https://www.bizmatch.com.au/business/${listing.id}/`,
              name: listing.title
            }))
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.bizmatch.com.au/' },
            { '@type': 'ListItem', position: 2, name: 'Explore', item: buildCanonicalUrl() }
          ]
        })}</script>
      </Helmet>
      <Header />
      <main className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">
            {sectorFilter !== 'all' && stateFilter !== 'all' 
              ? `${sectorFilter} Businesses in ${stateFilter}`
              : sectorFilter !== 'all' 
              ? `${sectorFilter} Businesses for Sale`
              : stateFilter !== 'all'
              ? `Businesses for Sale in ${stateFilter}`
              : 'Explore Businesses for Sale'}
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl">
            {sectorFilter !== 'all' || stateFilter !== 'all'
              ? `Browse verified ${sectorFilter !== 'all' ? sectorFilter.toLowerCase() : ''} business opportunities${stateFilter !== 'all' ? ` in ${stateFilter}` : ''}. Each listing includes detailed financial information.`
              : 'Discover verified business opportunities across Australia with comprehensive financial details and insights.'}
          </p>
        </div>

        {/* Search & Filters */}
        <div className="mb-8 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search businesses by title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Filter Toggle (Mobile) */}
          <div className="sm:hidden">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="w-full flex items-center justify-between"
            >
              <span className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
              </span>
              <SlidersHorizontal className="h-4 w-4" />
            </Button>
          </div>

          {/* Filters (Desktop & Mobile when expanded) */}
          <div className={`grid gap-4 sm:grid-cols-2 lg:grid-cols-5 ${showFilters ? 'block' : 'hidden sm:grid'}`}>
            {/* Sector Filter */}
            <Select value={sectorFilter} onValueChange={setSectorFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Sectors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sectors</SelectItem>
                {availableSectors.map((sector) => (
                  <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* State Filter */}
            <Select value={stateFilter} onValueChange={setStateFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All States" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {availableStates.map((state) => (
                  <SelectItem key={state} value={state}>{state}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Price Min */}
            <Input
              type="number"
              placeholder="Min Price"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
            />

            {/* Price Max */}
            <Input
              type="number"
              placeholder="Max Price"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
            />

            {/* Sort (Desktop) */}
            <div className="hidden sm:block">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="price-low">Price: Low → High</SelectItem>
                  <SelectItem value="price-high">Price: High → Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Clear Filters */}
            {activeFiltersCount > 0 && (
              <Button
                variant="outline"
                onClick={clearAllFilters}
                className="flex items-center gap-2 sm:w-auto"
              >
                <X className="h-4 w-4" />
                Clear All
              </Button>
            )}
          </div>

          {/* Active Filters Summary */}
          {activeFiltersCount > 0 && (
            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">
                {activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''} active
                {searchTerm && ` • Searching: "${searchTerm}"`}
                {sectorFilter !== 'all' && ` • Sector: ${sectorFilter}`}
                {stateFilter !== 'all' && ` • State: ${stateFilter}`}
                {(minPrice || maxPrice) && ` • Price: ${minPrice || '0'} - ${maxPrice || '∞'}`}
              </p>
            </div>
          )}
        </div>

        {/* Results Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <p className="text-muted-foreground">
              {loading ? 'Loading...' : `${totalCount} business${totalCount !== 1 ? 'es' : ''} found${currentPageFromUrl > 1 ? ` (Page ${currentPageFromUrl} of ${totalPages})` : ''}`}
            </p>
            <Button variant="default" size="default" asChild className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium px-6 py-2 h-10">
              <Link to="/swipe">
                <Sparkles className="h-4 w-4 mr-2" />
                Use Swipe Mode
              </Link>
            </Button>
          </div>

          {loading ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="overflow-hidden">
                  <div className="aspect-video">
                    <Skeleton className="w-full h-full" />
                  </div>
                  <CardHeader>
                    <Skeleton className="h-4 w-20 mb-2" />
                    <Skeleton className="h-6 w-full mb-2" />
                    <Skeleton className="h-4 w-3/4" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-4 w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : displayedListings.length === 0 ? (
            <div className="text-center py-12">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No businesses found</h3>
                <p className="text-muted-foreground mb-4">
                  Try adjusting your search criteria or filters
                </p>
                {activeFiltersCount > 0 && (
                  <Button variant="outline" onClick={clearAllFilters}>
                    Clear All Filters
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {displayedListings.map((listing) => (
                <Card 
                  key={listing.id} 
                  data-explore-card
                  className="group overflow-hidden hover:shadow-large transition-all duration-300 hover:-translate-y-1 cursor-pointer relative"
                  onClick={() => navigate(`/business/${listing.id}`)}
                >
                  {/* Favorite Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 z-10 bg-background/80 backdrop-blur-sm hover:bg-background"
                    onClick={(e) => toggleFavorite(listing.id, e)}
                  >
                    <Heart
                      className={`h-5 w-5 ${favoriteIds.has(listing.id) ? 'fill-red-500 text-red-500' : ''}`}
                    />
                  </Button>

                  {/* Image */}
                  <div className="aspect-video overflow-hidden bg-muted">
                    <ListingImage
                      src={getListingImage(listing as any)}
                      alt={listing.title ?? 'Business image'}
                      className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${listing.listing_number === '51180' ? 'object-top' : ''}`}
                      loading="lazy"
                    />
                  </div>

                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        {listing.listing_number && (
                          <Badge variant="outline" className="text-xs font-mono">#{listing.listing_number}</Badge>
                        )}
                        <Badge variant="secondary" className="text-xs">
                          {listing.sector}
                        </Badge>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-primary">
                          {formatPrice(listing.price)}
                        </p>
                      </div>
                    </div>
                    <CardTitle className="text-lg line-clamp-2 group-hover:text-primary transition-colors">
                      {listing.title}
                    </CardTitle>
                    {(teaserMap[String(listing.id)] || listing.teaser || listing.description) && (
                      <CardDescription className="text-sm line-clamp-2">
                        {truncateText(teaserMap[String(listing.id)] || listing.teaser || listing.description || '')}
                      </CardDescription>
                    )}
                  </CardHeader>

                  <CardContent className="pt-0">
                    <div className="space-y-2 mb-4">
                      {listing.location_suburb && listing.location_state && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{listing.location_suburb}, {listing.location_state}</span>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {listing.revenue && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <DollarSign className="h-3 w-3" />
                            <span>Rev: ${(listing.revenue / 1000)}k</span>
                          </div>
                        )}
                        {listing.ebitda && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <TrendingUp className="h-3 w-3" />
                            <span>EBITDA: ${(listing.ebitda / 1000)}k</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <Link to={`/business/${listing.id}`} onClick={(e) => e.stopPropagation()}>
                      <Button 
                        variant="outline" 
                        className="w-full group-hover:border-primary group-hover:text-primary transition-colors"
                        size="sm"
                      >
                        View Details
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <Button
                variant="outline"
                onClick={() => goToPage(currentPageFromUrl - 1)}
                disabled={currentPageFromUrl <= 1}
              >
                Previous
              </Button>
              <div className="flex gap-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((page) => (
                  <Button
                    key={page}
                    variant={page === currentPageFromUrl ? 'default' : 'outline'}
                    onClick={() => goToPage(page)}
                    className="w-10"
                  >
                    {page}
                  </Button>
                ))}
                {totalPages > 5 && (
                  <>
                    <span className="flex items-center px-2">...</span>
                    <Button
                      variant={currentPageFromUrl === totalPages ? 'default' : 'outline'}
                      onClick={() => goToPage(totalPages)}
                      className="w-10"
                    >
                      {totalPages}
                    </Button>
                  </>
                )}
              </div>
              <Button
                variant="outline"
                onClick={() => goToPage(currentPageFromUrl + 1)}
                disabled={currentPageFromUrl >= totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default Explore
